import { makeResetStyles, makeStyles, mergeClasses, shorthands } from '@fluentui/react-components';
import { tokens, typographyStyles } from '@fluentui-copilot/tokens';
import type { CitationSlots, CitationState } from './Citation.types';
import type { SlotClassNames } from '@fluentui/react-components';

export const citationClassNames: SlotClassNames<CitationSlots> = {
  root: 'fai-Citation',
};

const useCitationBaseClassName = makeResetStyles({
  display: 'inline-flex',
  justifyContent: 'center',
  boxSizing: 'border-box',
  alignItems: 'center',
  cursor: 'pointer',
  ...typographyStyles.caption2Strong,

  backgroundColor: tokens.colorNeutralBackground3,
  border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  color: tokens.colorNeutralForeground2,

  minWidth: '14px',
  height: '14px',
  verticalAlign: `calc((${typographyStyles.caption2Strong.lineHeight} - ${typographyStyles.caption2Strong.fontSize}) / 2)`,
  paddingInline: tokens.spacingHorizontalXXS,

  borderRadius: tokens.borderRadiusMedium,

  textDecoration: 'none',

  marginLeft: tokens.spacingHorizontalXXS,
  marginRight: tokens.spacingHorizontalXXS,

  ':hover': {
    cursor: 'pointer',
    color: tokens.colorBrandForeground2Hover,
    borderColor: tokens.colorBrandStroke2Hover,
    backgroundColor: tokens.colorBrandBackground2Hover,
  },

  ':hover:active': {
    cursor: 'pointer',
    color: tokens.colorBrandForeground2Pressed,
    borderColor: tokens.colorBrandStroke2Pressed,
    backgroundColor: tokens.colorBrandBackground2Pressed,
  },
});

const useCitationRootStyles = makeStyles({
  isBlock: {
    display: 'flex',
    marginLeft: 0,
    marginRight: 0,
  },
  isPopoverLocked: {
    color: tokens.colorBrandForeground2Pressed,
    ...shorthands.borderColor(tokens.colorBrandStroke2Pressed),
    backgroundColor: tokens.colorBrandBackground2,
    ':hover': {
      color: tokens.colorBrandForeground2Pressed,
      ...shorthands.borderColor(tokens.colorBrandStroke2Pressed),
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
});

const useNextStyles = makeStyles({ root: { borderRadius: tokens.borderRadiusXLarge } });

/**
 * Apply styling to the Citation slots based on the state
 */
export const useCitationStyles_unstable = (state: CitationState): CitationState => {
  'use no memo';

  const { designVersion } = state;

  const rootBaseClassName = useCitationBaseClassName();
  const rootStyles = useCitationRootStyles();
  const nextStyles = useNextStyles();

  state.root.className = mergeClasses(
    citationClassNames.root,
    state.isPopoverLocked && rootStyles.isPopoverLocked,
    state.block && rootStyles.isBlock,
    rootBaseClassName,
    designVersion === 'next' && nextStyles.root,
    state.root.className,
  );

  return state;
};
